import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check, Pencil, Printer, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import {
    useDecideGovernanceApproval,
    useGovernanceForms,
    useGovernanceRecord,
} from '../../hooks/useApi';
import RecordModal from './RecordModal';
import { FormSummary } from './FormRenderer';
import {
    Avatar,
    Badge,
    Button,
    Card,
    CardBody,
    CardHeader,
    DataTable,
    DetailRow,
    EmptyState,
    ErrorState,
    ListSkeleton,
    Modal,
    PageHeader,
    Skeleton,
    StatusBadge,
    Textarea,
    FormField,
    Input,
} from '../../components/ui';

const RATING_VARIANT = { low: 'neutral', medium: 'info', high: 'warning', critical: 'danger' };
const DECISION_VARIANT = {
    pending: 'neutral',
    approved: 'success',
    rejected: 'danger',
    not_required: 'neutral',
};

const formatDate = (value) =>
    value
        ? new Date(value).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
        : '—';

const RecordDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const { data: record, isLoading, isError } = useGovernanceRecord(id);
    const decide = useDecideGovernanceApproval();
    const { data: formsData } = useGovernanceForms();
    const [isEditOpen, setIsEditOpen] = useState(false);

    const [decisionTarget, setDecisionTarget] = useState(null);
    const [decisionForm, setDecisionForm] = useState({ decision: 'approved', decision_note: '', evidence_ref: '' });

    const openDecision = (approval, decision) => {
        setDecisionTarget(approval);
        setDecisionForm({ decision, decision_note: '', evidence_ref: '' });
    };

    const submitDecision = async () => {
        try {
            await decide.mutateAsync({
                id: decisionTarget.id,
                recordId: id,
                data: decisionForm,
            });
            setDecisionTarget(null);
        } catch {
            /* the mutation surfaces its own error toast */
        }
    };

    if (isLoading) {
        return (
            <>
                <Skeleton w={300} h={26} className="mb-5" />
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <Card className="lg:col-span-2">
                        <CardBody>
                            <ListSkeleton rows={6} />
                        </CardBody>
                    </Card>
                    <Card>
                        <CardBody>
                            <ListSkeleton rows={4} />
                        </CardBody>
                    </Card>
                </div>
            </>
        );
    }

    if (isError || !record) {
        return (
            <Card>
                <ErrorState
                    title="Record not found"
                    description="It may have been deleted, or you may not have access to it."
                    onRetry={() => navigate('/governance')}
                />
            </Card>
        );
    }

    const actions = record.actions || [];

    return (
        <>
            <PageHeader
                title={record.title}
                breadcrumbs={[
                    { label: 'Governance', to: '/governance' },
                    { label: record.record_ref },
                ]}
                actions={
                    <>
                        <Button icon={Printer} className="no-print" onClick={() => window.print()}>
                            Print
                        </Button>
                        <Button
                            variant="primary"
                            icon={Pencil}
                            className="no-print"
                            onClick={() => setIsEditOpen(true)}
                        >
                            Edit
                        </Button>
                    </>
                }
            >
                <div className="flex flex-wrap items-center gap-2 mt-2">
                    <StatusBadge status={record.status} />
                    {record.rating && (
                        <Badge variant={RATING_VARIANT[record.rating] || 'neutral'}>{record.rating} risk</Badge>
                    )}
                    <span className="text-label font-mono text-ink-500">{record.record_ref}</span>
                    <span className="text-ink-300" aria-hidden="true">·</span>
                    <span className="text-label text-ink-600">
                        {record.form_code} v{record.form_version}
                    </span>
                </div>
            </PageHeader>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
                <div className="lg:col-span-2 space-y-4">
                    <FormSummary form={record.form} values={record.payload} />

                    <Card>
                        <CardHeader title="Gaps & Actions" subtitle={`${actions.length} recorded`} />
                        <DataTable
                            columns={[
                                { key: 'observation', header: 'Gap / observation' },
                                {
                                    key: 'action',
                                    header: 'Action',
                                    render: (a) => a.action || <span className="text-muted">—</span>,
                                },
                                {
                                    key: 'owner_name',
                                    header: 'Owner',
                                    render: (a) => a.owner_name || <span className="text-muted">—</span>,
                                },
                                {
                                    key: 'target_date',
                                    header: 'Target',
                                    render: (a) => (
                                        <span className="tabular whitespace-nowrap text-ink-600">
                                            {formatDate(a.target_date)}
                                        </span>
                                    ),
                                },
                                {
                                    key: 'status',
                                    header: 'Status',
                                    align: 'right',
                                    render: (a) => <StatusBadge status={a.status} />,
                                },
                            ]}
                            rows={actions}
                            empty={
                                <EmptyState
                                    compact
                                    title="No gaps identified"
                                    description="Nothing on this record requires follow-up."
                                />
                            }
                        />
                    </Card>
                </div>

                <div className="space-y-4">
                    <Card>
                        <CardHeader title="Record details" />
                        <CardBody className="divide-y divide-[var(--divider-color)] py-2">
                            <DetailRow label="Reference">
                                <span className="font-mono">{record.record_ref}</span>
                            </DetailRow>
                            <DetailRow label="Form">
                                {record.form?.name || record.form_code}
                            </DetailRow>
                            <DetailRow label="Owner dept.">{record.owner_department}</DetailRow>
                            <DetailRow label="Date raised">
                                <span className="tabular">{formatDate(record.raised_date)}</span>
                            </DetailRow>
                            <DetailRow label="Target date">
                                <span className="tabular">{formatDate(record.target_date)}</span>
                            </DetailRow>
                            <DetailRow label="Raised by">
                                {record.created_by_name ? (
                                    <span className="flex items-center gap-2">
                                        <Avatar name={record.created_by_name} size="sm" />
                                        {record.created_by_name}
                                    </span>
                                ) : null}
                            </DetailRow>
                        </CardBody>
                    </Card>

                    <Card>
                        <CardHeader
                            title="Approval / verification"
                            subtitle="Every line must be settled before the record is approved."
                        />
                        <ul className="divide-y divide-[var(--divider-color)]">
                            {record.approvals?.map((a) => (
                                <li key={a.id} className="px-5 py-3">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <p className="text-base font-medium text-ink-900">{a.role_label}</p>
                                            {a.assignee_name && (
                                                <p className="text-micro text-ink-500 truncate">{a.assignee_name}</p>
                                            )}
                                        </div>
                                        <Badge variant={DECISION_VARIANT[a.decision] || 'neutral'}>
                                            {a.decision.replace('_', ' ')}
                                        </Badge>
                                    </div>

                                    {a.decision !== 'pending' && (
                                        <p className="text-micro text-ink-500 mt-1 tabular">
                                            {a.decided_by_name} · {formatDate(a.decided_at)}
                                            {a.evidence_ref ? ` · ${a.evidence_ref}` : ''}
                                        </p>
                                    )}
                                    {a.decision_note && (
                                        <p className="text-label text-ink-600 mt-1">{a.decision_note}</p>
                                    )}

                                    {a.decision === 'pending' && user && (
                                        <div className="flex gap-2 mt-2 no-print">
                                            <Button size="sm" icon={Check} onClick={() => openDecision(a, 'approved')}>
                                                Approve
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                icon={X}
                                                onClick={() => openDecision(a, 'rejected')}
                                            >
                                                Reject
                                            </Button>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </Card>
                </div>
            </div>

            <RecordModal
                isOpen={isEditOpen}
                onClose={() => setIsEditOpen(false)}
                form={record.form}
                record={record}
                ownerOptions={formsData?.options?.ict_teams || []}
            />

            <Modal
                isOpen={Boolean(decisionTarget)}
                onClose={() => setDecisionTarget(null)}
                title={decisionForm.decision === 'approved' ? 'Approve this line' : 'Reject this line'}
                subtitle={decisionTarget?.role_label}
                size="sm"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setDecisionTarget(null)}>
                            Cancel
                        </Button>
                        <Button
                            variant={decisionForm.decision === 'approved' ? 'primary' : 'danger'}
                            onClick={submitDecision}
                            loading={decide.isPending}
                        >
                            {decisionForm.decision === 'approved' ? 'Approve' : 'Reject'}
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <FormField label="Decision / verification note" htmlFor="gv-note">
                        <Textarea
                            id="gv-note"
                            rows={3}
                            value={decisionForm.decision_note}
                            onChange={(e) =>
                                setDecisionForm({ ...decisionForm, decision_note: e.target.value })
                            }
                            placeholder="What was checked, and on what basis?"
                        />
                    </FormField>

                    <FormField
                        label="Signature / evidence reference"
                        htmlFor="gv-evidence"
                        hint="Register entry, ticket number or attachment reference."
                    >
                        <Input
                            id="gv-evidence"
                            value={decisionForm.evidence_ref}
                            onChange={(e) =>
                                setDecisionForm({ ...decisionForm, evidence_ref: e.target.value })
                            }
                        />
                    </FormField>
                </div>
            </Modal>
        </>
    );
};

export default RecordDetail;
